/**
 * api-client.ts — Typed fetch wrapper for the Maloum CRM NestJS API.
 *
 * Security (T-04-32 / ARCH §10.3):
 *   - credentials: 'include' sends the httpOnly session cookie on every request
 *   - No Maloum URL — only our gateway (T-04-33)
 *   - Errors parsed via the NestJS SafeExceptionFilter shape
 *
 * Usage: import { api } from './api-client'
 *        const me = await api.get<MeDto>('/auth/me')
 */

import type {
  LoginDto,
  MeDto,
  AccountDto,
  AccountPatchDto,
  RemovedAccountDto,
  WipeEarningsResultDto,
  ReauthDto,
  ReauthResultDto,
  SetPasswordDto,
  AuditQueryDto,
  AuditRowDto,
  VerifyChainResult,
  OnboardingRowDto,
  BulkOnboardingResponse,
  InviteOperatorDto,
  OperatorRow,
  AcceptInvitationDto,
  RequestPasswordResetDto,
  CompletePasswordResetDto,
  ChangePasswordDto,
  EditDisplayNameDto,
} from '@crm/types';

// ---------------------------------------------------------------------------
// Base
// ---------------------------------------------------------------------------

/** The API base URL — empty string so /api proxied by Vite dev server or Nginx */
const BASE = '/api';

/** NestJS error shape from SafeExceptionFilter / built-in exception filter */
interface ApiError {
  statusCode: number;
  message: string | string[];
  error?: string;
  errors?: Record<string,string>;
}

// Post bot contracts intentionally contain no upstream media URLs or server imports.
export type PostBotLaneName = 'everyone' | 'subscribers';
export interface PostBotLaneInput {lane:PostBotLaneName;enabled:boolean;folderId:string|null;recurrenceFreq:'daily'|'weekly';postsPerDay:number;recurrenceDays:number[];windowStartMin:number|null;windowEndMin:number|null}
export interface PostBotConfigInput { selectedCategoryIds?: string[]; captionCase?: 'lowercase'|'standard';paused:boolean;timezone:string;handoffLeadHours:number;lanes:PostBotLaneInput[]}
export interface PostBotPipeline {revision:string;stage:'queued'|'loading_media'|'planning'|'retrying'|'blocked'|'complete'|'paused';attempt:number;updatedAt:string;nextRetryAt?:string;error?:string;planned?:number}
export interface PostBotConfig extends PostBotConfigInput {accountId:string;username:string;configured:boolean;activated:boolean;pipeline:PostBotPipeline|null;captionsEnabled:boolean;publishingEnabled:boolean;createdAt:string|null;updatedAt:string|null;lastPlannedAt:string|null;systemEnabled:boolean;mediaEnabled:boolean;subscription:{state:'unknown'|'priced'|'no_price';reason:string|null};lanes:(PostBotLaneInput & {id:string|null;status:string;statusReason:string|null;folderName:string|null;lastMaterializedThrough:string|null})[]}
export interface PostBotMutation {changed:boolean;planningQueued:boolean;disabledReason:string|null;refreshQueued?:boolean}
export interface PostBotCaptionMutation {changed:boolean;captionQueued?:boolean;disabledReason?:string|null}
export interface PostBotPublishMutation {changed:boolean;status:string;revision:number}
export interface PostBotPublishEdit {revision:number;caption?:string;scheduledAt?:string;categoryIds?:string[]}
export interface PostBotHealth {captionControls?:{postMicros:number;dailyMicros:number;dailyUsedMicros:number;maxWritingAttempts:number;budgetWaitingCount:number;pausedUntil:string|null;pauseReason:string|null};enabled:boolean;publishingArmed:boolean;liveGatesApproved:boolean;uncertainCount:number;stalledCount:number;recentFailureCount:number;oldestUncertainScheduledAt:string|null;pendingAlertCount:number;failedAlertCount:number;alertsEnabled:boolean;alertRecipientConfigured:boolean;status:'disabled'|'publishing_disabled'|'acceptance_pending'|'attention'|'ok'}
export interface PostBotFolder {id:string;name:string;pictureCount:number;videoCount:number}
export interface PostBotCategory {id:string;name:string;type:string;eligibleLanes:PostBotLaneName[];defaultFor:PostBotLaneName[]}
export interface PostBotSnapshot<T> {error?:string|null;refreshing?:boolean;items:T[];observedAt:string|null;status:'fresh'|'stale'|'unknown'|'error'}
export interface PostBotRosterRow {pipeline:PostBotPipeline|null;accountId:string;username:string;displayName:string|null;lastPublishedAt:string|null;publicationObservedAt:string|null;active:boolean;configured:boolean;paused:boolean;lastPlannedAt:string|null;nextSlot:string|null;queuedCount:number;lanes:{lane:PostBotLaneName;status:string;degrades:string[];queuedCount:number;nextSlot:string|null;lastPublishedAt:string|null;publicationObservedAt:string|null;enabled:boolean;recurrenceFreq:'daily'|'weekly';postsPerDay:number;recurrenceDays:number[];windowStartMin:number|null;windowEndMin:number|null}[];status:string;degrades:string[]}
export interface PostBotQueueRow {captionAttempts?:number;captionNextAttemptAt?:string|null;captionFailureDetails?:string[];id:string;accountId:string;lane:PostBotLaneName;scheduledAt:string;uploadId:string;mediaType:'picture'|'video';state:string;categoryIds:string[];failureReason:string|null;thumbnailUrl:string|null;caption:string|null;publishRevision?:number;publishOperation?:{kind:string;status:string;reason?:string|null}|null;publishDisabledReason?:string|null;publishNextAttemptAt?:string|null;publishLastObservedAt?:string|null}
export interface PostBotPage<T> {items:T[];nextCursor:string|null;mediaEnabled:boolean}
export const postBotKeys = {all:['post-bot'] as const,roster:['post-bot','roster'] as const,account:(id:string)=>['post-bot','account',id] as const};
const postBotPath = (id:string) => `/post-bot/accounts/${encodeURIComponent(id)}`;
export const postBotApi = {
  health:()=>get<PostBotHealth>('/health/post-bot'),
  cancelPost:(id:string,postId:string,revision:number)=>post<PostBotPublishMutation>(`${postBotPath(id)}/posts/${encodeURIComponent(postId)}/cancel`,{revision}),
  editPost:(id:string,postId:string,body:PostBotPublishEdit)=>fetchJson<PostBotPublishMutation>('PATCH',`${postBotPath(id)}/posts/${encodeURIComponent(postId)}`,body),
  regenerateCaption:(id:string,postId:string)=>post<PostBotCaptionMutation>(`${postBotPath(id)}/posts/${encodeURIComponent(postId)}/regenerate`),
  swapPostMedia:(id:string,postId:string)=>post<PostBotCaptionMutation>(`${postBotPath(id)}/posts/${encodeURIComponent(postId)}/swap-media`),
  editPostCategories:(id:string,postId:string,categoryIds:string[])=>fetchJson<PostBotCaptionMutation>('PUT',`${postBotPath(id)}/posts/${encodeURIComponent(postId)}/categories`,{categoryIds}),
  roster:(cursor?:string)=>get<PostBotPage<PostBotRosterRow>>(`/post-bot/accounts?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}`:''}`),
  config:(id:string)=>get<PostBotConfig>(`${postBotPath(id)}/config`),
  save:(id:string,body:PostBotConfigInput)=>fetchJson<PostBotMutation>('PUT',`${postBotPath(id)}/config`,body),
  bootstrap:(id:string)=>post<PostBotMutation>(`${postBotPath(id)}/bootstrap`),
  action:(id:string,action:'pause'|'resume'|'plan-now'|'refresh-folders')=>post<PostBotMutation>(`${postBotPath(id)}/${action}`),
  folders:(id:string)=>get<PostBotSnapshot<PostBotFolder>>(`${postBotPath(id)}/folders`),
  categories:(id:string)=>get<PostBotSnapshot<PostBotCategory>>(`${postBotPath(id)}/categories`),
  queue:(id:string,cursor?:string)=>get<PostBotPage<PostBotQueueRow>>(`${postBotPath(id)}/queue?limit=25${cursor ? `&cursor=${encodeURIComponent(cursor)}`:''}`),
};

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(
      typeof body.message === 'string'
        ? body.message
        : body.message.join('; '),
    );
    this.name = 'ApiRequestError';
  }
}

async function fetchJson<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...opts.headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...opts,
  });

  if (!res.ok) {
    let errorBody: ApiError;
    try {
      errorBody = (await res.json()) as ApiError;
    } catch {
      errorBody = { statusCode: res.status, message: res.statusText };
    }
    throw new ApiRequestError(res.status, errorBody);
  }

  // Success with no body: 204 No Content, or any empty-body success — e.g. a
  // void handler that NestJS serves as 201 with no content (POST /users/invite,
  // logout, refresh, …). Read as text first so an empty body doesn't make
  // res.json() throw "Unexpected end of JSON input", which previously surfaced
  // as a false "…failed" error even though the request succeeded.
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

const get = <T>(path: string, opts?: RequestInit) =>
  fetchJson<T>('GET', path, undefined, opts);

const post = <T>(path: string, body?: unknown, opts?: RequestInit) =>
  fetchJson<T>('POST', path, body, opts);

const patch = <T>(path: string, body?: unknown, opts?: RequestInit) =>
  fetchJson<T>('PATCH', path, body, opts);

const del = (path: string, opts?: RequestInit) =>
  fetchJson<void>('DELETE', path, undefined, opts);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const authApi = {
  login: (dto: LoginDto) => post<MeDto>('/auth/login', dto),
  logout: () => post<void>('/auth/logout'),
  me: () => get<MeDto>('/auth/me'),
  refresh: () => post<void>('/auth/refresh'),
};

// ---------------------------------------------------------------------------
// Accounts (admin-only)
// ---------------------------------------------------------------------------

export const accountsApi = {
  list: () => get<AccountDto[]>('/accounts'),
  get: (id: string) => get<AccountDto>(`/accounts/${id}`),
  patch: (id: string, dto: AccountPatchDto) =>
    patch<AccountDto>(`/accounts/${id}`, dto),
  reauth: (id: string, dto: ReauthDto) =>
    post<ReauthResultDto>(`/accounts/${id}/reauth`, dto),
  /** POST /accounts/:id/password — explicit password update (always re-mints; bulk dialog). */
  setPassword: (id: string, dto: SetPasswordDto) =>
    post<ReauthResultDto>(`/accounts/${id}/password`, dto),
  /** DELETE /accounts/:id — soft-remove an account (admin-only). */
  remove: (id: string) => del(`/accounts/${id}`),
  /** GET /accounts/removed — soft-deleted accounts + remaining earnings data. */
  listRemoved: () => get<RemovedAccountDto[]>('/accounts/removed'),
  /** POST /accounts/:id/wipe-earnings — permanently delete a removed account's tx rows. */
  wipeEarnings: (id: string) =>
    post<WipeEarningsResultDto>(`/accounts/${id}/wipe-earnings`),
};

// ---------------------------------------------------------------------------
// Onboarding (admin-only)
// ---------------------------------------------------------------------------

export const onboardingApi = {
  addSingle: (dto: OnboardingRowDto) =>
    post<BulkOnboardingResponse>('/accounts', dto),
  addBulkJson: (rows: OnboardingRowDto[]) =>
    post<BulkOnboardingResponse>('/accounts/bulk', { rows }),
  addBulkCsv: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${BASE}/accounts/bulk`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    }).then(async (res) => {
      if (!res.ok) {
        const err = (await res.json()) as ApiError;
        throw new ApiRequestError(res.status, err);
      }
      return res.json() as Promise<BulkOnboardingResponse>;
    });
  },
};

// ---------------------------------------------------------------------------
// Assignments (admin-only)
// ---------------------------------------------------------------------------

export interface AssignmentRow {
  id: string;
  chatterId: string;
  accountId: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  createdBy: string;
  createdAt: string;
}

/** A chatter (operator) row for the Assignments editor + display-name surfacing (Phase 9 D-09-16). */
export interface ChatterDto {
  id: string;
  email: string;
  role: 'admin' | 'chatter';
  displayName: string;
  status: 'active';
}

export const assignmentsApi = {
  list: () => get<AssignmentRow[]>('/assignments'),
  create: (dto: { chatterId: string; accountId: string; effectiveFrom?: string }) =>
    post<AssignmentRow>('/assignments', dto),
  delete: (id: string) => del(`/assignments/${id}`),
};

export const chattersApi = {
  list: () => get<ChatterDto[]>('/chatters'),
};

// ---------------------------------------------------------------------------
// Health (per-account)
// ---------------------------------------------------------------------------

export interface AccountHealthDto {
  accountId: string;
  state: string;
  reason?: string;
  ts?: number;
  alive: boolean;
}

/** Per-account entry in the fleet sync-freshness snapshot. */
export interface AccountFreshnessDto {
  accountId: string;
  label: string | null;
  lastSyncAt: number | null;
  ageMs: number | null;
  stale: boolean;
}

/**
 * Fleet sync-freshness snapshot (GET /health/sync, admin-only).
 *
 * Answers "is anything actually reaching Maloum right now?" — the signal that was missing when a
 * proxy traffic cap froze the whole fleet for three days (2026-08-21) behind green badges.
 */
export interface SyncFreshnessDto {
  monitored: boolean;
  fleetStalled: boolean;
  checkedAt: number;
  staleAfterMs: number;
  expectedAccounts: number;
  staleAccounts: number;
  neverSyncedAccounts: number;
  newestSyncAt: number | null;
  newestSyncAgeMs: number | null;
  accounts: AccountFreshnessDto[];
}

export const healthApi = {
  getAccount: (id: string) =>
    get<AccountHealthDto>(`/accounts/${id}/health`),
  getSyncFreshness: () => get<SyncFreshnessDto>('/health/sync'),
};

// ---------------------------------------------------------------------------
// Audit (admin-only)
// ---------------------------------------------------------------------------

export const auditApi = {
  list: (params: AuditQueryDto = {}) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.actor) qs.set('actor', params.actor);
    if (params.action) qs.set('action', params.action);
    if (params.target) qs.set('target', params.target);
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.limit) qs.set('limit', String(params.limit));
    return get<AuditRowDto[]>(`/audit?${qs.toString()}`);
  },
  verifyChain: (from?: string, to?: string) =>
    post<VerifyChainResult>('/audit/verify', { from, to }),
  exportUrl: (params: AuditQueryDto = {}) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return `${BASE}/audit/export?${qs.toString()}`;
  },
};

// ---------------------------------------------------------------------------
// KPI (admin + chatter, with server-side financial redaction for chatters)
// ---------------------------------------------------------------------------

export interface CreatorKpiDto {
  creatorId: string;
  creatorName: string;
  revenue_gross_minor: number;
  unattributed_gross_minor: number;
  ppv_unlocks: number;
  fan_count: number;
  arpu_minor: number;
}

export interface TopSpenderDto {
  fanId: string;
  maloum_member_id: string;
  username: string | null;
  creatorId: string;
  creatorName: string;
  total_spend_gross_minor: number;
  ppv_count: number;
  /** Present only for admins — stripped by FinancialRedactionInterceptor for chatters (T-05-28). */
  is_whale?: boolean;
  last_activity: string | null;
}

/**
 * One row in the flat 4-way kind breakdown returned by GET /kpi/breakdown.
 * The route returns KindBreakdownEntry[] (flat, one per creator+kind).
 * The dashboard aggregates across creators client-side.
 */
export interface KindBreakdownEntry {
  kind: string;
  gross_minor: number;
  net_minor: number;
  payout_minor: number;
  count: number;
}

/** Parameters for GET /kpi/transactions. */
export interface KpiTransactionsParams {
  next?: string;
  limit?: number;
  since?: string;
  until?: string;
  /** Multi-select kind filter — serialized comma-separated into ?kind= (owner ask 2026-07-02). */
  kinds?: string[];
  status?: string;
  creatorIds?: string[];
}

/** One transaction row returned by GET /kpi/transactions. */
export interface TransactionRowDto {
  id: string;
  maloum_transaction_id: string;
  payer_maloum_id: string | null;
  payee_maloum_id: string | null;
  occurred_at: string;
  category: string | null;
  payment_method: string | null;
  tx_status: string | null;
  amount_gross_minor: number;
  amount_net_minor: number | null;
  /** Creator payout (after Maloum's commission) — Maloum's headline number. */
  amount_payout_minor: number | null;
  currency: string;
  /** Creator display_name — always joined (CR-02 fix). */
  creatorName: string;
  /** Creator UUID — always joined (CR-02 fix). */
  creatorId: string;
  /** Fan username from fan_profile (null when fan not in CRM). */
  fanUsername: string | null;
  /** Fan nickname from fan_profile (null when fan not in CRM). */
  fanNickname: string | null;
}

/** Paginated response from GET /kpi/transactions. */
export interface TransactionPageDto {
  next: string | null;
  data: TransactionRowDto[];
}

export const kpiApi = {
  creators: (since?: string, until?: string, creatorIds?: string[]) => {
    const qs = new URLSearchParams();
    if (since) qs.set('since', since);
    if (until) qs.set('until', until);
    if (creatorIds?.length) qs.set('creatorIds', creatorIds.join(','));
    const query = qs.toString();
    return get<CreatorKpiDto[]>(`/kpi/creators${query ? `?${query}` : ''}`);
  },
  topSpenders: (limit = 20, since?: string, until?: string, creatorIds?: string[]) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (since) qs.set('since', since);
    if (until) qs.set('until', until);
    if (creatorIds?.length) qs.set('creatorIds', creatorIds.join(','));
    return get<TopSpenderDto[]>(`/kpi/top-spenders?${qs.toString()}`);
  },
  /** GET /kpi/transactions — paginated fan transaction ledger (admin-only). D-07-09. */
  transactions: (params: KpiTransactionsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.next) qs.set('next', params.next);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.since) qs.set('since', params.since);
    if (params.until) qs.set('until', params.until);
    if (params.kinds?.length) qs.set('kind', params.kinds.join(','));
    if (params.status) qs.set('status', params.status);
    if (params.creatorIds?.length) qs.set('creatorIds', params.creatorIds.join(','));
    return get<TransactionPageDto>(`/kpi/transactions?${qs.toString()}`);
  },
  /** GET /kpi/breakdown — 4-way kind breakdown (admin-only). D-07-02. */
  breakdown: (since?: string, until?: string, creatorIds?: string[]) => {
    const qs = new URLSearchParams();
    if (since) qs.set('since', since);
    if (until) qs.set('until', until);
    if (creatorIds?.length) qs.set('creatorIds', creatorIds.join(','));
    return get<KindBreakdownEntry[]>(`/kpi/breakdown?${qs.toString()}`);
  },
};

// ---------------------------------------------------------------------------
// Broadcasts (admin-only sign-off-gated drip, D-05-16)
// ---------------------------------------------------------------------------

export interface CampaignDto {
  id: string;
  accountId: string;
  createdBy: string;
  approvedBy: string | null;
  approvedAt: string | null;
  name: string;
  messageEn: string;
  messageDe: string | null;
  isPpv: boolean;
  status: string;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  pausedAt: string | null;
  completedAt: string | null;
  etaSeconds: number | null;
  createdAt: string;
}

export interface CreateCampaignBody {
  name: string;
  messageEn: string;
  accountId: string;
  // Phase 8 targeting + scheduling:
  includeLists?: string[];
  excludeLists?: string[];
  scheduledAt?: string | null;
  messageKind?: 'text' | 'free_media' | 'chat_product';
  recipientCap?: number;
  // existing:
  targetTags?: string[];
  minSpendEur?: number;
  isPpv?: boolean;
  ppvPriceMinor?: number;
  ppvPriceCurrency?: string;
  vaultMediaId?: string;
}

/** A Maloum fan segment / list row returned by GET /broadcasts/chat-lists. */
export interface MaloumListDto {
  /** CR-02 FIX: server returns ChatListDto.id (not _id) — aligned to server field name. */
  id: string;
  name: string;
  /** Friendly relabel applied server-side (D-08-04a). isManaged system lists get human names. */
  friendlyName: string;
  isManaged: boolean;
  totalMemberCount: number;
}

export const broadcastsApi = {
  list: (accountId: string) =>
    get<CampaignDto[]>(`/broadcasts?accountId=${encodeURIComponent(accountId)}`),
  get: (id: string) => get<CampaignDto>(`/broadcasts/${id}`),
  create: (body: CreateCampaignBody) => post<CampaignDto>('/broadcasts', body),
  approve: (id: string) => post<CampaignDto>(`/broadcasts/${id}/approve`),
  pause: (id: string, reason?: string) =>
    post<CampaignDto>(`/broadcasts/${id}/pause`, { reason }),
  resume: (id: string) => post<CampaignDto>(`/broadcasts/${id}/resume`),
  cancel: (id: string) => post<void>(`/broadcasts/${id}/cancel`),
};

/** Per-account Maloum list/segment picker (D-08-04). */
export const listsApi = {
  getChatLists: (accountId: string) =>
    get<MaloumListDto[]>(`/broadcasts/chat-lists?accountId=${encodeURIComponent(accountId)}`),
};

// ---------------------------------------------------------------------------
// Operators (Phase 9 — D-09-17 / D-09-18)
// Routes: POST /users/invite, GET /users, DELETE /users/invitations/:id,
//         DELETE /users/:id, POST /users/accept-invitation (@Public),
//         POST /users/reset-password/request (@Public),
//         POST /users/reset-password/complete (@Public),
//         GET /users/me, PATCH /users/me/password, PATCH /users/me/display-name
// ---------------------------------------------------------------------------

export interface PendingInvitationRow {
  id: string;
  email: string;
  role: 'admin' | 'chatter';
  invitedBy: string;
  expiresAt: string;
  status: 'pending';
  createdAt: string;
}

export const operatorsApi = {
  /** POST /users/invite — send an invitation (admin-only). */
  invite: (dto: InviteOperatorDto) =>
    post<void>('/users/invite', dto),

  /** GET /users — list active operators + pending invitations (admin-only). */
  list: () => get<OperatorRow[]>('/users'),

  /** DELETE /users/invitations/:id — revoke a pending invitation (admin-only). */
  revokeInvitation: (id: string) => del(`/users/invitations/${id}`),

  /** DELETE /users/:id — soft-delete/remove an operator (admin-only). */
  remove: (id: string) => del(`/users/${id}`),

  /** GET /users/me — self-service profile (any authenticated). */
  me: () => get<MeDto>('/users/me'),

  /** PATCH /users/me/password — change own password (any authenticated). */
  changePassword: (dto: ChangePasswordDto) =>
    patch<void>('/users/me/password', dto),

  /** PATCH /users/me/display-name — edit own display name (any authenticated). */
  editDisplayName: (dto: EditDisplayNameDto) =>
    patch<void>('/users/me/display-name', dto),

  /** POST /users/accept-invitation — public; accept invite + set pw + display name. */
  acceptInvitation: (dto: AcceptInvitationDto) =>
    post<void>('/users/accept-invitation', dto),

  /** POST /users/reset-password/request — public; always 200 (non-enumerating). */
  requestReset: (dto: RequestPasswordResetDto) =>
    post<void>('/users/reset-password/request', dto),

  /** POST /users/reset-password/complete — public; consume token + set new password. */
  completeReset: (dto: CompletePasswordResetDto) =>
    post<void>('/users/reset-password/complete', dto),
};

// ---------------------------------------------------------------------------
// Vault (admin — read-only, for broadcast media picker)
// ---------------------------------------------------------------------------

export interface VaultThumbnailDto {
  uploadId: string;
  url: string;
}

export interface AdminVaultFolderDto {
  id: string;
  name: string;
  isManaged: boolean;
  pictureCount: number;
  videoCount: number;
  thumbnails: VaultThumbnailDto[];
}

export interface AdminVaultFolderPageDto {
  folders: AdminVaultFolderDto[];
  next: string | number | null;
}

export interface AdminVaultMediaItemDto {
  /** Durable id — use as mediaId in PPV send body. */
  uploadId: string;
  type: string;
  /** Short-TTL signed URL — display only, NEVER persist. */
  url: string;
  width: number;
  height: number;
  length?: number;
  thumbnail?: { url: string; uploadId?: string };
}

export interface AdminVaultMediaPageDto {
  items: AdminVaultMediaItemDto[];
  next: string | number | null;
}

export const adminVaultApi = {
  folders: (accountId: string) =>
    get<AdminVaultFolderPageDto>(`/vault/folders?accountId=${encodeURIComponent(accountId)}`),
  media: (accountId: string, folderId: string) =>
    get<AdminVaultMediaPageDto>(`/vault/folders/${encodeURIComponent(folderId)}/media?accountId=${encodeURIComponent(accountId)}`),
};

// ---------------------------------------------------------------------------
// Named default export bundle (convenience)
// ---------------------------------------------------------------------------

export const api = {
  auth: authApi,
  accounts: accountsApi,
  onboarding: onboardingApi,
  assignments: assignmentsApi,
  chatters: chattersApi,
  health: healthApi,
  audit: auditApi,
  kpi: kpiApi,
  broadcasts: broadcastsApi,
  operators: operatorsApi,
  lists: listsApi,
  adminVault: adminVaultApi,
};
